import { useParams, Link } from 'react-router-dom';
import { getProductById } from '../../mocks/products';
import SharedTrustBar from '../../components/feature/SharedTrustBar';
import ProductDetailHero from './components/ProductDetailHero';
import ProductDetailBreakdown from './components/ProductDetailBreakdown';
import MidCTA from '../../components/feature/MidCTA';
import SharedAIBlock from '../../components/feature/SharedAIBlock';
import SharedHowItWorks from '../../components/feature/SharedHowItWorks';
import SharedWhyChooseUs from '../../components/feature/SharedWhyChooseUs';
import ProductDetailUseCases from './components/ProductDetailUseCases';
import SharedTestimonials from '../../components/feature/SharedTestimonials';
import SharedFAQ from '../../components/feature/SharedFAQ';
import SharedFinalCTA from '../../components/feature/SharedFinalCTA';
import { productDetailAIItems, repairSteps, whyReasons } from '../../mocks/shared-sections';
import { productFaqs } from '../../mocks/faqs';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const product = getProductById(id ?? '');

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <p className="text-stone-500 text-lg mb-4">Product not found.</p>
          <Link to="/products" className="text-[#0B5C4D] font-bold hover:text-[#FF6B35] cursor-pointer">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      {/* 1. Hero */}
      <ProductDetailHero product={product} />
      {/* 2. Trust Bar */}
      <SharedTrustBar />
      {/* 3. Product Breakdown */}
      <ProductDetailBreakdown product={product} />
      {/* Mid CTA */}
      <MidCTA
        headline="Ready to Fix Your Board?"
        sub="Add to cart and ship today. Repairs take minutes."
        btnLabel={`Buy ${product.name} — $${product.price}`}
        btnTo={`/products/${product.slug}`}
        urgency="Free Micro Kit with orders over $40"
        variant="teal"
      />
      {/* 4. AI Answer Block */}
      <SharedAIBlock
        title="About This Repair Kit"
        items={productDetailAIItems}
        bgClass="bg-white"
      />
      {/* 5. How It Works */}
      <SharedHowItWorks
        title="How to Use This Kit"
        subtitle="Follow these 5 steps for a professional-grade repair in under 15 minutes."
        steps={repairSteps}
        bgClass="bg-[#F5EFE6]"
      />
      {/* 6. Why Choose Us */}
      <SharedWhyChooseUs
        reasons={whyReasons}
        showComparison={true}
        bgClass="bg-white"
      />
      {/* Mid CTA after Why Choose Us */}
      <MidCTA
        headline="Convinced? Get Yours Shipped Today."
        sub="Same-day shipping on orders before noon."
        btnLabel="Add to Cart"
        urgency="Join 40,000+ surfers who trust PHIX Doctor"
        variant="orange"
      />
      {/* 7. Use Cases / Global */}
      <ProductDetailUseCases currentProductId={product.id} />
      {/* 8. Testimonials */}
      <SharedTestimonials />
      {/* 9. FAQ */}
      <SharedFAQ
        faqs={productFaqs}
        title="Product FAQ"
        subtitle="Common questions about using this kit and how to get the best repair results."
        bgClass="bg-white"
      />
      {/* 10. Final CTA */}
      <SharedFinalCTA
        headline="Don't Let a Ding"
        headlineAccent="Ruin Your Session."
        primaryLabel={`Buy ${product.name}`}
        primaryTo={`/products/${product.slug}`}
        secondaryLabel="View All Products"
        secondaryTo="/products"
        urgency="Don't let a ding decide whether you surf today."
      />
    </main>
  );
}
